import type { AutomationMode, Preset } from '../../core/types';
import type { ToolSpec } from '../../core/types';
import { getFeatureFlags } from '../../core/featureFlags';
import type { AutomationFeatureFlags } from '../../core/featureFlags';
import { AUTOMATION_PROMPT_DEFAULTS } from '../../../server/src/modules/operations/automationPrompts.js';
import { imagePolicyInstructions } from './imagePolicy';
import { ttsPolicyInstructions } from './ttsPolicy';
import { markerMomentPublications, momentPublicationInstructions, momentPublicationTool, readMomentPublication } from '../../../server/src/modules/moments/publication.js';
import type { MomentPublication } from '../../../server/src/modules/moments/publication.js';

export const IMAGE_MARKER_PREFIX = '{{zhixinpic[';
export const TTS_MARKER_PREFIX = '{{zhixintts[';
export const BUBBLE_MARKER_PREFIX = '{{zhixinbubble[';
export const MOMENT_MARKER_PREFIX = '{{zhixinmoment[';
export const MOMENT_CHECK_MARKER_PREFIX = '{{zhixinmomentcheck[';
export const MOMENT_PUBLICATION_MARKER_PREFIX = '{{zhixinpost[';

const COMPLETE_MARKER = /\{\{zhixin(?:pic|tts|bubble|moment|momentcheck|post)\[[\s\S]*?\]zhixin(?:pic|tts|bubble|moment|momentcheck|post)\}\}/gi;
const IMAGE_MARKER = /\{\{zhixinpic\[([\s\S]*?)\]zhixinpic\}\}/gi;
const TTS_MARKER = /\{\{zhixintts\[([\s\S]*?)\]zhixintts\}\}/gi;
const BUBBLE_MARKER = /\{\{zhixinbubble\[([\s\S]*?)\]zhixinbubble\}\}/gi;
const MOMENT_MARKER = /\{\{zhixinmoment\[([\s\S]*?)\]zhixinmoment\}\}/gi;
const MOMENT_CHECK_MARKER = /\{\{zhixinmomentcheck\[([\s\S]*?)\]zhixinmomentcheck\}\}/gi;
const PREFIXES = [IMAGE_MARKER_PREFIX, TTS_MARKER_PREFIX, BUBBLE_MARKER_PREFIX, MOMENT_MARKER_PREFIX, MOMENT_CHECK_MARKER_PREFIX, MOMENT_PUBLICATION_MARKER_PREFIX];

export interface ParsedRichOutput {
  text: string;
  imagePrompts: string[];
  ttsRequested: boolean;
  bubbleDelayMinutes: number | null;
  momentDelayMinutes: number | null;
  momentCheckDelayMinutes?: number | null;
  momentPublications?: MomentPublication[];
}

export interface StreamToolCall {
  index: number;
  id?: string;
  name?: string;
  arguments: string;
}

export interface CompletedToolCall {
  id: string;
  name: string;
  arguments: Record<string, unknown>;
}

export function mergeToolCallDelta(
  current: Map<number, StreamToolCall>,
  delta: string,
): void {
  let parsed: Partial<StreamToolCall>;
  try {
    parsed = JSON.parse(delta) as Partial<StreamToolCall>;
  } catch {
    return;
  }
  const index = Number.isInteger(parsed.index) ? Number(parsed.index) : 0;
  const call = current.get(index) ?? { index, arguments: '' };
  if (typeof parsed.id === 'string' && parsed.id !== '') call.id = parsed.id;
  if (typeof parsed.name === 'string' && parsed.name !== '') {
    call.name = call.name && !parsed.name.startsWith(call.name) ? call.name + parsed.name : parsed.name;
  }
  if (typeof parsed.arguments === 'string') call.arguments += parsed.arguments;
  current.set(index, call);
}

export function completedToolCalls(value: ReadonlyMap<number, StreamToolCall>): CompletedToolCall[] {
  const calls: CompletedToolCall[] = [];
  for (const call of [...value.values()].sort((a, b) => a.index - b.index)) {
    if (!call.name) continue;
    let args: Record<string, unknown> = {};
    if (call.arguments.trim() !== '') {
      try {
        const parsed = JSON.parse(call.arguments) as unknown;
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) args = parsed as Record<string, unknown>;
      } catch {
        continue;
      }
    }
    calls.push({ id: call.id ?? `tool_${call.index}`, name: call.name, arguments: args });
  }
  return calls;
}

export function applyControlToolCalls(
  parsed: ParsedRichOutput,
  calls: readonly CompletedToolCall[],
): ParsedRichOutput {
  const next = { ...parsed, imagePrompts: [...parsed.imagePrompts] };
  for (const call of calls) {
    if (call.name === 'zhixin_publish_moment') {
      const publication = readMomentPublication(call.arguments);
      if (publication) next.momentPublications = [...(next.momentPublications ?? []), publication];
    } else if (call.name === 'zhixin_generate_image') {
      const prompt = typeof call.arguments.prompt === 'string' ? call.arguments.prompt.trim() : '';
      if (prompt) next.imagePrompts.push(prompt.slice(0, 4000));
    } else if (call.name === 'zhixin_send_voice') {
      next.ttsRequested = true;
      const text = typeof call.arguments.text === 'string' ? call.arguments.text.trim() : '';
      if (next.text.trim() === '' && text !== '') next.text = text;
    } else if (call.name === 'zhixin_schedule_bubble' || call.name === 'zhixin_schedule_moment' || call.name === 'zhixin_schedule_moment_check') {
      const raw = call.arguments.minutes;
      const minutes = Number(raw);
      if (Number.isInteger(minutes) && minutes >= 1 && minutes <= 1440) {
        if (call.name === 'zhixin_schedule_bubble') next.bubbleDelayMinutes = minutes;
        else if (call.name === 'zhixin_schedule_moment') next.momentDelayMinutes = minutes;
        else next.momentCheckDelayMinutes = minutes;
      }
    }
  }
  return next;
}

export function buildRoleTools(
  preset: Preset,
  options: { autoImage: boolean; autoTts: boolean; autoText: boolean; allowSchedules?: boolean },
  features: AutomationFeatureFlags = getFeatureFlags(),
): ToolSpec[] {
  const tools: ToolSpec[] = [];
  if (options.autoImage && preset.autoImageEnabled) {
    tools.push({
      name: 'zhixin_generate_image',
      description: ['Generate one image and attach it to the current character reply.', imagePolicyInstructions(preset, features.imageInitiative)].filter(Boolean).join(' '),
      inputSchema: {
        type: 'object',
        properties: { prompt: { type: 'string', description: 'A complete, concrete image generation prompt.' } },
        required: ['prompt'],
        additionalProperties: false,
      },
    });
  }
  if (options.autoTts && preset.autoTtsEnabled) {
    tools.push({
      name: 'zhixin_send_voice',
      description: ['Synthesize the current character reply as a voice message in addition to the text.', ttsPolicyInstructions(preset, features.ttsInitiative)].filter(Boolean).join(' '),
      inputSchema: {
        type: 'object',
        properties: { text: { type: 'string', minLength: 1, description: 'The complete character reply to speak. Include the reply here even when no text message is emitted.' } },
        required: ['text'],
        additionalProperties: false,
      },
    });
  }
  if (options.autoText && options.allowSchedules !== false) {
    tools.push(momentPublicationTool);
    if (automationAllowsBubble(preset.automationMode)) {
      tools.push({
        name: 'zhixin_schedule_bubble',
        description: 'Schedule a proactive character message for later. Avoid configured quiet periods.',
        inputSchema: {
          type: 'object',
          properties: { minutes: { type: 'integer', minimum: 1, maximum: 1440 } },
          required: ['minutes'],
          additionalProperties: false,
        },
      });
    }
    if (automationAllowsMoment(preset.automationMode)) {
      tools.push({
        name: 'zhixin_schedule_moment',
        description: 'Proactively consider sharing your own life, personality and recent experiences in Moments. When you decide to publish, actually call this tool to schedule the post. Merely promising to post does not publish anything. Never postpone an already scheduled post.',
        inputSchema: {
          type: 'object',
          properties: { minutes: { type: 'integer', minimum: 1, maximum: 1440 } },
          required: ['minutes'],
          additionalProperties: false,
        },
      });
      tools.push({
        name: 'zhixin_schedule_moment_check',
        description: 'Choose not to publish a Moments post yet and schedule when to reconsider. You may freely decide not to post; there is no required posting frequency. Call this tool with the number of minutes until your next decision.',
        inputSchema: {
          type: 'object',
          properties: { minutes: { type: 'integer', minimum: 1, maximum: 1440 } },
          required: ['minutes'],
          additionalProperties: false,
        },
      });
    }
  }
  return tools;
}

function collectMatches(pattern: RegExp, value: string): string[] {
  const matches: string[] = [];
  for (const match of value.matchAll(new RegExp(pattern.source, pattern.flags))) {
    if (match[1] !== undefined) matches.push(match[1]);
  }
  return matches;
}

function delayMinutes(value: string): number | null {
  const parsed = Number(value.trim());
  return Number.isInteger(parsed) && parsed >= 1 && parsed <= 1440 ? parsed : null;
}

/** Remove complete control blocks and any trailing, incomplete marker fragment. */
export function stripControlMarkers(value: string): string {
  let cleaned = value.replace(COMPLETE_MARKER, '');
  const lastOpen = cleaned.lastIndexOf('{{');
  if (lastOpen >= 0) {
    const tail = cleaned.slice(lastOpen).toLowerCase();
    if (PREFIXES.some((prefix) => prefix.toLowerCase().startsWith(tail) || tail.startsWith(prefix.toLowerCase()))) {
      cleaned = cleaned.slice(0, lastOpen);
    }
  }
  return cleaned.replace(/[ \t]+\n/g, '\n').trimEnd();
}

export function parseRichOutput(value: string): ParsedRichOutput {
  const images = collectMatches(IMAGE_MARKER, value)
    .map((prompt) => prompt.trim().slice(0, 4000))
    .filter((prompt) => prompt !== '');
  const tts = collectMatches(TTS_MARKER, value).some((body) => body.trim().toUpperCase() === 'ON');
  const bubble = collectMatches(BUBBLE_MARKER, value)
    .map(delayMinutes)
    .filter((minutes): minutes is number => minutes !== null)
    .at(-1) ?? null;
  const moment = collectMatches(MOMENT_MARKER, value)
    .map(delayMinutes)
    .filter((minutes): minutes is number => minutes !== null)
    .at(-1) ?? null;
  return {
    text: stripControlMarkers(value).trim(),
    imagePrompts: images,
    ttsRequested: tts,
    bubbleDelayMinutes: bubble,
    momentDelayMinutes: moment,
    momentPublications: markerMomentPublications(value),
    momentCheckDelayMinutes: collectMatches(MOMENT_CHECK_MARKER, value).map(delayMinutes)
      .filter((minutes): minutes is number => minutes !== null).at(-1) ?? null,
  };
}

export function automationAllowsBubble(mode: AutomationMode | undefined): boolean {
  return mode === 'auto_bubble' || mode === 'auto_bubble_moment';
}

export function automationAllowsMoment(mode: AutomationMode | undefined): boolean {
  return mode === 'auto_moment' || mode === 'auto_bubble_moment';
}

function quietLines(preset: Preset): string {
  const periods = preset.quietPeriods ?? [];
  if (periods.length === 0) return '无';
  return periods.map((period) => `${period.start}-${period.end}`).join('、');
}

/**
 * Instructions are injected only for capabilities the role can actually use.
 * Markers are part of the character protocol and must never be repeated to the user.
 */
export function buildAutomationInstructions(
  preset: Preset,
  options: {
    autoText: boolean;
    autoImage: boolean;
    autoTts: boolean;
    now?: Date;
    allowSchedules?: boolean;
  },
  features: AutomationFeatureFlags = getFeatureFlags(),
): string {
  const prompts = features.automationPrompts ?? AUTOMATION_PROMPT_DEFAULTS;
  const lines: string[] = [];
  let available = false;
  if (options.autoImage && preset.autoImageEnabled === true) {
    available = true;
    lines.push(prompts.imageProtocol);
    const policy = imagePolicyInstructions(preset, features.imageInitiative);
    if (policy !== '') lines.push(policy);
  }
  if (options.autoTts && preset.autoTtsEnabled === true) {
    available = true;
    lines.push(prompts.voiceProtocol);
    const policy = ttsPolicyInstructions(preset, features.ttsInitiative);
    if (policy !== '') lines.push(policy);
  }
  if (options.autoText && options.allowSchedules !== false && prompts.roleProtocol.trim() !== '') {
    available = true;
    lines.push(momentPublicationInstructions);
  }
  if (options.autoText && options.allowSchedules !== false
    && (automationAllowsBubble(preset.automationMode) || automationAllowsMoment(preset.automationMode))) {
    available = true;
    const now = options.now ?? new Date();
    lines.push(prompts.timeContext.replaceAll('{localTime}', () => now.toLocaleString('zh-CN', { hour12: false })));
    lines.push(prompts.quietContext.replaceAll('{quietPeriods}', () => quietLines(preset)));
    if (automationAllowsBubble(preset.automationMode)) {
      lines.push(prompts.bubbleSchedule);
    }
    if (automationAllowsMoment(preset.automationMode)) {
      lines.push(prompts.momentSchedule);
    }
  }
  return available ? [prompts.roleProtocol, ...lines].filter(Boolean).join('\n') : '';
}
